import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import CalendarConnect from "@/components/CalendarConnect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "enrollments", label: "Enrollments", testid: "admin-tab-enrollments" },
  { key: "inquiries", label: "Inquiries", testid: "admin-tab-inquiries" },
  { key: "bookings", label: "Bookings", testid: "admin-tab-bookings" },
  { key: "payments", label: "Payments", testid: "admin-tab-payments" },
];

const NEXT_STATUS = { new: "contacted", contacted: "closed", closed: "new" };
const STATUS_STYLE = {
  new: "text-amber-600 border-amber-400/40 bg-amber-400/10",
  contacted: "text-sky-600 border-sky-400/40 bg-sky-400/10",
  closed: "text-emerald-600 border-emerald-400/40 bg-emerald-400/10",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem("vf_admin_token") || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem("vf_admin_token"));
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [tab, setTab] = useState("enrollments");
  const [data, setData] = useState({ enrollments: [], inquiries: [], bookings: [], payments: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${t}` };
    try {
      const [en, inq, bk, pay] = await Promise.all([
        axios.get(`${API}/admin/enrollments`, { headers }),
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/payments`, { headers }),
      ]);
      setData({ enrollments: en.data, inquiries: inq.data, bookings: bk.data, payments: pay.data });
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setUser(r.data);
        load(token);
      })
      .catch(() => {
        localStorage.removeItem("vf_admin_token");
        setToken("");
      })
      .finally(() => setChecking(false));
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem("vf_admin_token", data.access_token);
      setChecking(true);
      setToken(data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    localStorage.removeItem("vf_admin_token");
    setUser(null);
    setToken("");
  };

  const cycleStatus = async (kind, id, current) => {
    try {
      await axios.patch(`${API}/admin/leads/${kind}/${id}`, { status: NEXT_STATUS[current] || "new" }, { headers: { Authorization: `Bearer ${token}` } });
      load(token);
    } catch {
      toast.error("Could not update status");
    }
  };

  const inputCls =
    "w-full bg-[#ffffff] border border-slate-200 rounded-xl px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 transition-colors";

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={32} className="text-amber-600 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="absolute inset-0 hero-grid-bg pointer-events-none -z-10" />
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-slate-200 bg-[#F8FAFC] p-8 space-y-4">
          <div className="mb-6">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-2">Restricted</p>
            <h1 className="font-display text-2xl font-bold tracking-tight">Admin Sign In</h1>
          </div>
          <input
            type="email"
            required
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            placeholder="Admin email"
            className={inputCls}
            data-testid="admin-login-email-input"
          />
          <input
            type="password"
            required
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            placeholder="Password"
            className={inputCls}
            data-testid="admin-login-password-input"
          />
          {error && (
            <p className="text-xs text-red-600" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="w-full bg-amber-400 text-slate-900 font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-amber-500 transition-colors"
            data-testid="admin-login-submit-button"
          >
            Sign In
          </button>
          <Link to="/" className="block text-center text-xs text-slate-500 hover:text-slate-700 transition-colors">
            Back to site
          </Link>
        </form>
      </div>
    );
  }

  const rows = data[tab];

  return (
    <div className="min-h-screen px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-1">Leads Dashboard</p>
            <h1 className="font-display text-3xl font-bold tracking-tight">Welcome, {user.name || "Admin"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <CalendarConnect token={token} />
            <button
              onClick={() => load(token)}
              className="flex items-center gap-2 border border-slate-200 rounded-full px-5 py-2.5 text-xs text-slate-700 hover:border-amber-400/50 transition-colors"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 border border-slate-200 rounded-full px-5 py-2.5 text-xs text-slate-700 hover:border-red-400/50 hover:text-red-300 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`font-mono2 text-xs tracking-wider px-5 py-2.5 rounded-full border transition-all ${
                tab === t.key ? "bg-amber-400 text-slate-900 border-amber-400 font-bold" : "border-slate-200 text-slate-600 hover:text-amber-700"
              }`}
            >
              {t.label} ({data[t.key].length})
            </button>
          ))}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-[#F8FAFC] overflow-hidden">
          {rows.length === 0 ? (
            <p className="p-12 text-center text-sm text-slate-500" data-testid="admin-empty-state">
              {loading ? "Loading..." : "No records yet."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`admin-table-${tab}`}>
                <thead>
                  <tr className="border-b border-slate-200 text-left font-mono2 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-4">{tab === "bookings" ? "Session" : "Date"}</th>
                    <th className="px-5 py-4">{tab === "payments" ? "Customer" : "Name"}</th>
                    {tab !== "payments" && <th className="px-5 py-4">Email</th>}
                    <th className="px-5 py-4">{tab === "inquiries" ? "Type / Message" : tab === "bookings" ? "Note" : "Course"}</th>
                    {tab === "payments" && <th className="px-5 py-4">Amount</th>}
                    {tab === "enrollments" && <th className="px-5 py-4">Payment</th>}
                    {tab !== "payments" && <th className="px-5 py-4">Lead Status</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id || r.session_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4 text-slate-500 text-xs whitespace-nowrap">
                        {tab === "bookings" ? (
                          <>
                            <span className="text-slate-700">{r.date} • {r.time} IST</span>
                            <span className="block text-slate-500">{r.minutes} min</span>
                          </>
                        ) : (
                          fmtDate(r.created_at)
                        )}
                      </td>
                      <td className="px-5 py-4 text-slate-800">{r.name || r.customer_name}</td>
                      {tab !== "payments" && <td className="px-5 py-4 text-slate-600 text-xs">{r.email}</td>}
                      <td className="px-5 py-4 text-slate-600 text-xs max-w-[280px]">
                        {tab === "inquiries" ? (
                          <>
                            <span className="text-sky-700">{r.inquiry_type}</span>
                            <span className="block text-slate-500 truncate">{r.message}</span>
                          </>
                        ) : tab === "bookings" ? (
                          <>
                            <span className="text-amber-700">{r.session_title}</span>
                            {r.note && <span className="block text-slate-500 truncate">{r.note}</span>}
                          </>
                        ) : (
                          r.course_title
                        )}
                      </td>
                      {tab === "payments" && (
                        <td className="px-5 py-4 font-mono2 text-amber-600">
                          ${r.amount?.toLocaleString()}
                          <span className="ml-2 text-[9px] uppercase tracking-wider text-slate-500">{r.provider === "paypal" ? "PayPal" : "Card"}</span>
                        </td>
                      )}
                      {tab === "enrollments" && (
                        <td className="px-5 py-4">
                          <span
                            className={`font-mono2 text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border ${
                              r.status === "paid"
                                ? "text-emerald-600 border-emerald-400/40 bg-emerald-400/10"
                                : r.status === "enrolled_free"
                                ? "text-sky-600 border-sky-400/40 bg-sky-400/10"
                                : "text-slate-600 border-slate-200 bg-slate-100"
                            }`}
                          >
                            {r.status === "paid" ? "Paid" : r.status === "enrolled_free" ? "Free" : "Interest"}
                          </span>
                        </td>
                      )}
                      {tab !== "payments" && (
                        <td className="px-5 py-4">
                          <button
                            onClick={() => cycleStatus(tab, r.id, r.lead_status || "new")}
                            className={`font-mono2 text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border transition-colors ${STATUS_STYLE[r.lead_status || "new"]}`}
                            data-testid={`lead-status-button-${r.id}`}
                          >
                            {r.lead_status || "new"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
